/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box, Modal, Backdrop,
  Fade, Paper,
  Snackbar, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { translations } from 'src/constants/translations';
import { ButtonApp } from 'src/components/Button';
import {
  IComplex, IDeveloper, IFileUri, ISpecificationProp,
} from 'src/typings/complexes';
import { useGetAuthData } from 'src/hooks';
import {
  postComplexData, putComplexData, putComplexLocationData,
} from 'src/api';
import Tab from '@mui/material/Tab';
import { urlToBase64 } from 'src/helpers/urlToBase64';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { BoxFormAutocomplete } from 'src/components/profileComponents/FormAutocomplete';
import { LocationDetails } from 'src/components/profileComponents/Complexes/components/LocationDetails';
import { getComplexLocationsDefaultValues, getComplexSpecificationsDefaultValues } from 'src/helpers/complexEdit';
import {
  IDevelopmentSpecificationPropChange,
} from 'src/components/profileComponents/Complexes/components/ProjectSpecification/types';
import { BoxFormTextField } from 'src/components/profileComponents/FormTextField';
import { UploadSection } from 'src/components/profileComponents/UploadSection';
import { readURL } from 'src/helpers';
import { ProjectSpecification } from 'src/components/profileComponents/Complexes/components/ProjectSpecification';
import { RandomInteger } from 'src/helpers/randomInteger';
import { ComplexStatus } from 'src/constants/complexStatus';
import { useComplexStatus } from 'src/hooks/app/use-complex-status';
import { useComplexCreate } from 'src/components/profileComponents/Complexes/hooks/useComplexCreate';
import { buildOptionsOfEntity } from 'src/helpers/optionsOfEntity';
import { ILanguage } from 'src/typings/language';
import { ComplexInitialDataDeveloper, CreatEditComplexData } from 'src/components/profileComponents/Complexes/types';
import { useUser } from 'src/hooks/user';
import { TypeOfRole } from 'src/typings/user';
import { useStyles } from '../styles';

interface StatusModal {
  isOpen: boolean;
  handleChangeOpen: Function;
  refreshComplexes: Function;
  complex?: IComplex;
}

const schema = yup.object().shape({
  projectName: yup.string().required(translations.requiredField),
  projectShortName: yup.string().required(translations.requiredField),
  projectAddress: yup.string(),
  systemPrimaryLanguage: yup.string().required(translations.requiredField),
  projectCallBackCountryCode: yup.string(),
  projectLogo: yup.string(),
  projectPreviewImage: yup.string(),
});

export const ComplexModal: React.FC<StatusModal> = ({
  isOpen, handleChangeOpen, complex, refreshComplexes,
}) => {
  const styles = useStyles({});
  const [error, setError] = useState('');
  const jwt = useGetAuthData().getJWT();

  const {
    register, handleSubmit, setValue, reset, errors, getValues,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const [projectName, setProjectName] = useState<string>('');
  const [projectShortName, setProjectShortName] = useState<string>('');
  const [projectAddress, setProjectAddress] = useState<string>('');
  const [systemPrimaryLanguage, _setSystemPrimaryLanguage] = useState<IDevelopmentSpecificationPropChange<string> | undefined>();
  const [languagesCodesOptions, setLanguagesCodesOptions] = useState<IDevelopmentSpecificationPropChange<string>[]>([]);
  const [countriesCodesOptions, setCountriesCodesOptions] = useState<IDevelopmentSpecificationPropChange<string>[]>([]);
  const [developersOptions, setDevelopersOptions] = useState<IDevelopmentSpecificationPropChange<string>[]>([]);
  const { complexInitialData } = useComplexCreate();
  const [systemSecondaryLanguagesOptions, _setSystemSecondaryLanguagesOptions] = useState<IDevelopmentSpecificationPropChange<string>[]>([]);
  const [systemSecondaryLanguages, setSystemSecondaryLanguages] = useState<IDevelopmentSpecificationPropChange<string>[]>([]);
  const [projectCallBackCountryCode, setProjectCallBackCountryCode] = useState<IDevelopmentSpecificationPropChange<string> | undefined>();
  const [tabValue, setTabValue] = useState<string>('1');
  const [preview, setPreview] = useState<string | null>(null);
  const [previewUri, setPreviewUri] = useState<IFileUri | null>(null);
  const [logo, setLogo] = useState<string | null>(null);
  const [logoUri, setLogoUri] = useState<IFileUri | null>(null);
  const { complexStatusOptions, currentComplexStatus } = useComplexStatus(complex?.status);
  const [projectDeveloper, setProjectDeveloper] = useState<IDevelopmentSpecificationPropChange<string> | null>(
    buildOptionsOfEntity<IDeveloper>(complex?.developers, 'name', '@id', complex?.language)?.[0] || null,
  );
  const [projectStatus, setProjectStatus] = useState<IDevelopmentSpecificationPropChange<ComplexStatus> | undefined>(currentComplexStatus);
  const { currentUserRole } = useUser();

  useEffect(() => {
    if (complexInitialData) {
      setLanguagesCodesOptions(buildOptionsOfEntity<ILanguage>(complexInitialData.languageCodes, 'code', 'code'));
      setCountriesCodesOptions(buildOptionsOfEntity<ILanguage>(complexInitialData.countryCodes, 'code', 'code'));
      setDevelopersOptions(buildOptionsOfEntity<ComplexInitialDataDeveloper>(complexInitialData.developers, 'name', '@id'));
    }
  }, [complexInitialData]);

  useEffect(() => {
    if (languagesCodesOptions.length) {
      _setSystemSecondaryLanguagesOptions(languagesCodesOptions);
      _setAdminPanelSecondaryLanguagesOptions(languagesCodesOptions);
      _setWebsiteSecondaryLanguagesOptions(languagesCodesOptions);
    }
  }, [languagesCodesOptions]);

  const setSystemPrimaryLanguage = (currLanguage: string | null) => {
    _setSystemPrimaryLanguage(currLanguage ? { label: currLanguage, value: currLanguage } : undefined);
    _setSystemSecondaryLanguagesOptions(languagesCodesOptions.filter((language) => language.value !== currLanguage));
    setSystemSecondaryLanguages(complex?.secondaryLanguages?.map((item) => ({ label: item, value: item })) || []);
  };

  const [adminPanelPrimaryLanguage, _setAdminPanelPrimaryLanguage] = useState<IDevelopmentSpecificationPropChange<string> | undefined>();
  const [adminPanelSecondaryLanguagesOptions, _setAdminPanelSecondaryLanguagesOptions] = useState<IDevelopmentSpecificationPropChange<string>[]>([]);
  const [adminPanelSecondaryLanguages, setAdminPanelSecondaryLanguages] = useState<IDevelopmentSpecificationPropChange<string>[]>([]);

  const setAdminPanelPrimaryLanguage = (currLanguage: string | null) => {
    _setAdminPanelPrimaryLanguage(currLanguage ? { label: currLanguage, value: currLanguage } : undefined);
    _setAdminPanelSecondaryLanguagesOptions(languagesCodesOptions.filter((language) => language.value !== currLanguage));
    setAdminPanelSecondaryLanguages(complex?.crosstableSecondaryLanguages?.map((item) => ({ label: item, value: item })) || []);
  };

  const [websitePrimaryLanguage, _setWebsitePrimaryLanguage] = useState<IDevelopmentSpecificationPropChange<string> | undefined>();
  const [websiteSecondaryLanguagesOptions, _setWebsiteSecondaryLanguagesOptions] = useState<IDevelopmentSpecificationPropChange<string>[]>([]);
  const [websiteSecondaryLanguages, setWebsiteSecondaryLanguages] = useState<IDevelopmentSpecificationPropChange<string>[]>([]);

  const setWebsitePrimaryLanguage = (currLanguage: string | null) => {
    _setWebsitePrimaryLanguage(currLanguage ? { label: currLanguage, value: currLanguage } : undefined);
    _setWebsiteSecondaryLanguagesOptions(languagesCodesOptions.filter((language) => language.value !== currLanguage));
    setWebsiteSecondaryLanguages(complex?.moduleSecondaryLanguages?.map((item) => ({ label: item, value: item })) || []);
  };

  useEffect(() => {
    if (complex) {
      const formData = {
        projectName: complex.title[complex.language],
        projectShortName: complex.complexShortName,
        projectAddress: complex.address?.[complex.language] || '',
        systemPrimaryLanguage: complex.language,
      };
      setProjectName(complex.title[complex.language]);
      setProjectShortName(complex.complexShortName);
      setProjectAddress(complex.address?.[complex.language] || '');
      setProjectCallBackCountryCode(
        complex.clientModuleConfig.formDefaultCountry
          ? { label: complex.clientModuleConfig.formDefaultCountry, value: complex.clientModuleConfig.formDefaultCountry }
          : undefined,
      );

      setSystemPrimaryLanguage(complex.language);
      setAdminPanelPrimaryLanguage(complex.crosstableLanguage);
      setWebsitePrimaryLanguage(complex.moduleLanguage);

      urlToBase64(complex.previewFileUri?.fileUrl || '')
        .then((previewUriData) => {
          setPreview(String(previewUriData.base64Url));
        });
      urlToBase64(complex.logoFileUri?.fileUrl || '')
        .then((previewUriData) => {
          setLogo(String(previewUriData.base64Url));
        });
      reset(formData);
    }
  }, [complex]);

  const onSubmit = async (formData:any) => {
    const saveData: Partial<CreatEditComplexData> = {
      status: projectStatus?.value,
      title: complex?.title ? {
        ...complex.title,
        [String(systemPrimaryLanguage?.value)]: projectName,
      } : { [String(systemPrimaryLanguage?.value)]: projectName },
      complexShortName: projectShortName,
      address: complex?.address ? {
        ...complex?.address,
        [String(systemPrimaryLanguage?.value)]: projectAddress,
      } : { [String(systemPrimaryLanguage?.value)]: projectAddress },
      secondaryLanguages: systemSecondaryLanguages.map((language) => language.value),
      crosstableLanguage: adminPanelPrimaryLanguage?.value || null,
      crosstableSecondaryLanguages: adminPanelSecondaryLanguages.length ? adminPanelSecondaryLanguages.map((language) => language.value) : null,
      moduleLanguage: websitePrimaryLanguage?.value || null,
      moduleSecondaryLanguages: websiteSecondaryLanguages.length ? websiteSecondaryLanguages.map((language) => language.value) : null,
      location: complex?.location,
    };
    if (projectDeveloper) {
      saveData.developers = [projectDeveloper.value];
    }
    if (formData.developmentSpecification) {
      Object.keys(formData.developmentSpecification).forEach((key) => {
        if (key && formData.developmentSpecification[key]) {
          if (!saveData.developmentSpecification) {
            saveData.developmentSpecification = {};
          }
          // @ts-ignore
          saveData.developmentSpecification[key] = (
            formData.developmentSpecification[key]
            && Array.isArray(formData.developmentSpecification[key])
            && !formData.developmentSpecification[key].length
          ) ? [] : formData.developmentSpecification[key];
        }
      });
      if (saveData?.developmentSpecification && Object.keys(saveData.developmentSpecification).length) {
        saveData.developmentSpecification.id = complex?.developmentSpecification?.id || RandomInteger();
      }
    }
    if (formData.location) {
      Object.keys(formData.location).forEach((key) => {
        // @ts-ignore
        saveData.location[key] = formData.location[key] ? { [systemPrimaryLanguage?.value]: formData.location[key] } : null;
      });
      saveData.location.latitude = formData?.location?.latitude ? formData?.location?.latitude : null;
      saveData.location.longitude = formData?.location?.longitude ? formData?.location?.longitude : null;
    }
    if (systemPrimaryLanguage?.value) {
      saveData.language = systemPrimaryLanguage.value;
    }
    if (previewUri) {
      saveData.previewFileUri = previewUri;
    }
    if (logoUri) {
      saveData.logoFileUri = logoUri;
    }
    if (complex && projectCallBackCountryCode) {
      saveData.clientModuleConfig = {
        ...complex.clientModuleConfig,
        formDefaultCountry: projectCallBackCountryCode.value,
      };
    }

    let res;

    if (complex) {
      res = await putComplexData(jwt, complex.id, saveData);
    } else {
      res = await postComplexData(jwt, saveData);
    }

    if (res.ok) {
      handleChangeOpen(false);
      refreshComplexes();
    } else {
      setError(res.data['hydra:description']);
    }
  };

  const updateLocation = async () => {
    const formData = getValues();
    const saveData: Partial<CreatEditComplexData> = {
      latitude: formData?.location?.latitude ? formData?.location?.latitude : null,
      longitude: formData?.location?.longitude ? formData?.location?.longitude : null,
    };

    if (complex?.location['@id']) {
      const res = await putComplexLocationData(complex?.location['@id'], jwt, saveData);
      if (res.ok) {
        handleChangeOpen(false);
        refreshComplexes();
      } else {
        setError(res.data['hydra:description']);
      }
    }
  };

  const specificationsDefaultValues = getComplexSpecificationsDefaultValues(complex);

  const locationsDefaultValues = useMemo(() => getComplexLocationsDefaultValues(complex), [complex]);

  const developerPropertyChange = (data: ISpecificationProp[], propName: string) => {
    setValue(propName, data);
  };

  const developerLocationChange = (data: string, prop: string) => {
    setValue(prop, data);
  };

  const close = () => handleChangeOpen(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const _onUpload = useCallback(async (file: File, response: any, fileTypeSetter: React.Dispatch<React.SetStateAction<IFileUri | null>>, setter: React.Dispatch<React.SetStateAction<string | null>>) => {
    if (file && response.data[0]) {
      fileTypeSetter(response.data[0]);
      const base64: string = await readURL(file);
      if (base64) {
        setter(base64);
      }
    }
  }, [complex]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={styles.modalWindow}
      open={isOpen}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={isOpen}>
        <Paper
          style={{
            outline: 'none',
            zIndex: '1',
          }}
        >
          <Box width="800px" maxHeight="90vh" overflow="auto" p="52px">
            <Box mb={6} fontSize="24px" fontWeight={600} display="flex" alignItems="center" justifyContent="space-between">
              {complex ? translations.projectEditing : translations.createProject}
              <CloseIcon onClick={close} className={styles.closeBtn} />
            </Box>
            <Box fontSize="18px">

              <Box sx={{ width: '100%', typography: 'body1' }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <TabContext value={tabValue}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      <TabList onChange={handleTabChange} aria-label="basic tabs example">
                        <Tab label="MAIN INFORMATION" value="1" />
                        <Tab label="Location details" value="2" />
                        <Tab label="Complex specification" value="3" />
                      </TabList>
                    </Box>
                    <TabPanel value="1">
                      <Box flex="1" display="flex" gap="40px">
                        <BoxFormTextField
                          label={translations.projectName}
                          name="projectName"
                          inputRef={register}
                          defaultValue={projectName}
                          onChange={(event) => {
                            setProjectName(event.target.value);
                          }}
                          {...register('projectName')}
                          errors={errors}
                        />
                        <BoxFormAutocomplete
                          label={translations.projectStatus}
                          name="projectStatus"
                          options={complexStatusOptions}
                          value={projectStatus}
                          onChange={(option: IDevelopmentSpecificationPropChange<ComplexStatus>) => {
                            setProjectStatus(option);
                          }}
                          {...register('projectStatus')}
                        />
                      </Box>
                      <Box flex="1" display="flex" gap="40px">
                        <Box flex="1" display="flex" gap="40px">
                          <BoxFormAutocomplete
                            label={translations.developer}
                            name="developer"
                            disabled={currentUserRole === TypeOfRole.supervisor}
                            options={developersOptions}
                            defaultValue={projectDeveloper}
                            onChange={(option: IDevelopmentSpecificationPropChange<string>) => {
                              setProjectDeveloper(option);
                            }}
                            {...register('projectDeveloper')}
                          />
                          <BoxFormTextField
                            label="flatshow.property/"
                            name="projectShortName"
                            inputRef={register}
                            defaultValue={projectShortName}
                            onChange={(event) => {
                              setProjectShortName(event.target.value);
                            }}
                            {...register('projectShortName')}
                            errors={errors}
                          />
                        </Box>
                      </Box>
                      <Box flex="1" display="flex" gap="40px">
                        <Box width="60%">
                          <BoxFormTextField
                            label={translations.projectAddress}
                            name="projectAddress"
                            inputRef={register}
                            defaultValue={projectAddress}
                            onChange={(event) => {
                              setProjectAddress(event.target.value);
                            }}
                            {...register('projectAddress')}
                          />
                        </Box>
                        <Box width="40%">
                          <BoxFormAutocomplete
                            label={translations.projectCallBackCountryCode}
                            name="projectCallBackCountryCode"
                            options={countriesCodesOptions}
                            value={projectCallBackCountryCode}
                            onChange={(value: IDevelopmentSpecificationPropChange<string>) => {
                              setProjectCallBackCountryCode(value);
                            }}
                            {...register('projectCallBackCountryCode')}
                          />

                        </Box>
                      </Box>

                      <Box flex="1" display="flex" gap="40px">
                        <BoxFormAutocomplete
                          label={translations.systemPrimaryLanguage}
                          name="systemPrimaryLanguage"
                          options={languagesCodesOptions}
                          value={systemPrimaryLanguage}
                          onChange={(value: IDevelopmentSpecificationPropChange<string>) => {
                            setValue('systemPrimaryLanguage', value.value);
                            setSystemPrimaryLanguage(value.value);
                          }}
                          {...register('systemPrimaryLanguage')}
                          errors={errors}
                        />
                        <BoxFormAutocomplete
                          label={translations.systemSecondaryLanguages}
                          name="systemSecondaryLanguages"
                          options={systemSecondaryLanguagesOptions}
                          value={systemSecondaryLanguages}
                          onChange={setSystemSecondaryLanguages}
                          {...register('systemSecondaryLanguages')}
                        />
                      </Box>

                      <Box flex="1" display="flex" gap="40px">
                        <BoxFormAutocomplete
                          label={translations.adminPanelPrimaryLanguage}
                          name="adminPanelPrimaryLanguage"
                          options={languagesCodesOptions}
                          value={adminPanelPrimaryLanguage}
                          onChange={(value: IDevelopmentSpecificationPropChange<string>) => {
                            setAdminPanelPrimaryLanguage(value.value);
                          }}
                          {...register('adminPanelPrimaryLanguage')}
                        />
                        <BoxFormAutocomplete
                          label={translations.adminPanelSecondaryLanguages}
                          name="adminPanelSecondaryLanguages"
                          options={adminPanelSecondaryLanguagesOptions}
                          value={adminPanelSecondaryLanguages}
                          onChange={(value: IDevelopmentSpecificationPropChange<string>[]) => {
                            setAdminPanelSecondaryLanguages(value);
                          }}
                          {...register('adminPanelSecondaryLanguages')}
                        />
                      </Box>

                      <Box flex="1" display="flex" gap="40px">
                        <BoxFormAutocomplete
                          label={translations.websitePrimaryLanguage}
                          name="websitePrimaryLanguage"
                          options={languagesCodesOptions}
                          value={websitePrimaryLanguage}
                          onChange={(value: IDevelopmentSpecificationPropChange<string>) => {
                            setWebsitePrimaryLanguage(value.value);
                          }}
                          {...register('websitePrimaryLanguage')}
                        />
                        <BoxFormAutocomplete
                          label={translations.websiteSecondaryLanguages}
                          name="websiteSecondaryLanguages"
                          options={websiteSecondaryLanguagesOptions}
                          value={websiteSecondaryLanguages}
                          onChange={(value: IDevelopmentSpecificationPropChange<string>[]) => {
                            setWebsiteSecondaryLanguages(value);
                          }}
                          {...register('websiteSecondaryLanguages')}
                        />
                      </Box>

                      <Box flex="1" display="flex" gap="40px">
                        <div className={styles.uploadWrapper}>
                          <div
                            className={styles.upload}
                          >
                            <div>{ translations.uploadProjectPreviewImage }</div>
                            <UploadSection
                              image={preview}
                              onUpload={(file, response) => {
                                _onUpload(file, response, setPreviewUri, setPreview);
                              }}
                              onMoveToTrash={() => setPreview(null)}
                              register={register}
                              registerName="projectPreviewImage"
                            />
                          </div>
                        </div>
                        <div className={styles.uploadWrapper}>
                          <Box
                            className={styles.upload}
                          >
                            <div>{ translations.uploadProjectLogo }</div>
                            <UploadSection
                              image={logo}
                              onUpload={(file, response) => {
                                _onUpload(file, response, setLogoUri, setLogo);
                              }}
                              onMoveToTrash={() => setLogo(null)}
                              register={register}
                              registerName="projectLogo"
                            />
                          </Box>
                        </div>
                      </Box>

                    </TabPanel>
                    <TabPanel value="2">
                      <LocationDetails
                        complex={complex}
                        defaultValues={locationsDefaultValues}
                        register={register}
                        updateLocation={updateLocation}
                        onChange={(data, prop) => developerLocationChange(data, prop)}
                      />
                    </TabPanel>
                    <TabPanel value="3">
                      <ProjectSpecification
                        propName="developmentSpecification"
                        complex={complex}
                        defaultValues={specificationsDefaultValues}
                        register={register}
                        onChange={(data, prop) => developerPropertyChange(data, prop)}
                      />
                    </TabPanel>
                    <Box display="flex" justifyContent="flex-end" gap="20px">
                      <ButtonApp
                        onClick={close}
                        text={translations.cancel}
                        color="primary"
                        variant="outlined"
                        type="button"
                        className={styles.btn}
                      />
                      <ButtonApp
                        text={translations.saveChanges}
                        color="primary"
                        variant="contained"
                        type="submit"
                        className={styles.btn}
                      />
                    </Box>
                  </TabContext>
                </form>
              </Box>

              <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
                <Alert onClose={() => setError('')} elevation={6} variant="filled" severity="error">
                  { error }
                </Alert>
              </Snackbar>
            </Box>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
};
